/**
 * Dòng "Dịp chụp" của một đơn (QĐ 0480) — chi tiết đơn, khối "Đơn đang chat",
 * gọi xác nhận lịch, thẻ ca ekip.
 *
 * - Đơn đã có dịp: chỉ hiện dịp đã lưu + nút "Sửa" nhỏ — không hỏi lại (9a).
 * - Đơn chưa có: nút "Ghi dịp chụp / sinh nhật"; thấy chữ "sinh nhật 12/11"
 *   trong ghi chú thì thêm dòng gợi ý một chạm, mở hộp đã điền sẵn.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/ui/dialog";
import { ACTION } from "@/lib/auth/actions";
import { usePermission } from "@/lib/authv2/permissions";
import { findBirthdayMention } from "@/lib/birthday";
import { cn } from "@/lib/utils";
import { OccasionFields } from "./OccasionFields";
import { useOrderOccasion, usePutOrderOccasion, type OrderOccasion } from "./api";
import { EMPTY_DRAFT, draftFromMention, draftOf, draftToInput, occasionSoon, occasionSummary, type OccasionDraft } from "./model";

export function OccasionDialog({
  orderId,
  open,
  onOpenChange,
  initial,
}: {
  orderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: OccasionDraft;
}) {
  // Bản nháp nạp từ `initial` lúc dựng — nơi mở hộp đổi `key` mỗi lần mở.
  const [draft, setDraft] = useState(initial);
  const putM = usePutOrderOccasion(orderId);
  const ready = draftToInput(draft);
  const save = async () => {
    if ("error" in ready) return;
    try {
      await putM.mutateAsync(ready.input);
      toast.success(ready.input.kind ? "Đã lưu dịp chụp" : "Đã bỏ dịp chụp của đơn");
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Không lưu được dịp chụp");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Dịp chụp của đơn</DialogTitle>
          <DialogDescription>
            Sinh nhật của người đặt được lưu luôn vào hồ sơ khách. Bấm lại dịp đang chọn để bỏ chọn.
          </DialogDescription>
        </DialogHeader>
        <OccasionFields value={draft} onChange={setDraft} disabled={putM.isPending} />
        {"error" in ready ? <p className="text-xs text-muted-foreground">{ready.error}</p> : null}
        <DialogFooter className="grid grid-cols-2 gap-2 sm:flex">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={putM.isPending}>
            Đóng
          </Button>
          <Button type="button" onClick={() => void save()} disabled={putM.isPending || "error" in ready}>
            {putM.isPending ? "Đang lưu…" : "Lưu dịp chụp"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Nhãn ưu tiên hậu kỳ (8a) — rỗng khi dịp còn xa hoặc không có ngày. */
export function OccasionSoonBadge({
  occasion,
  className,
}: {
  occasion: OrderOccasion | null | undefined;
  className?: string | undefined;
}) {
  const soon = occasionSoon(occasion);
  if (!soon) return null;
  return (
    <span
      data-testid="dip-chup-sap-toi"
      className={cn(
        "inline-flex items-center rounded bg-pink-600 px-1.5 py-0.5 text-[11px] font-semibold text-white",
        className,
      )}
    >
      {soon.text}
    </span>
  );
}

export function OrderOccasionRow({
  orderId,
  occasion: given,
  suggestText,
  compact,
}: {
  orderId: string;
  /** Dịp đã đọc gom từ danh sách; vắng = tự đọc theo đơn. null = đã biết là chưa có. */
  occasion?: OrderOccasion | null | undefined;
  /** Chữ tự do để tìm "sinh nhật 12/11" (ghi chú đơn, sổ ghi chú). */
  suggestText?: string | null | undefined;
  compact?: boolean | undefined;
}) {
  const canWrite = usePermission(ACTION.OrderOccasionWrite);
  const q = useOrderOccasion(orderId, given === undefined);
  const occasion = given === undefined ? q.data : given;
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState<OccasionDraft>(EMPTY_DRAFT);
  const [openSeq, setOpenSeq] = useState(0);
  const mention = !occasion ? findBirthdayMention(suggestText) : null;

  const openWith = (d: OccasionDraft) => {
    setInitial(d);
    setOpenSeq((n) => n + 1);
    setOpen(true);
  };

  if (given === undefined && q.isLoading) return null;
  if (given === undefined && q.isError) {
    return (
      <p role="alert" className="text-xs text-[var(--color-danger-text)]">
        {q.error instanceof Error ? q.error.message : "Không tải được dịp chụp của đơn"}
      </p>
    );
  }

  return (
    <div data-testid="dip-chup" className={cn("space-y-1", compact ? "text-xs" : "text-sm")}>
      {occasion ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium">{occasionSummary(occasion)}</span>
          {occasion.note ? <span className="text-muted-foreground">· {occasion.note}</span> : null}
          <OccasionSoonBadge occasion={occasion} />
          {canWrite ? (
            <Button type="button" variant="link" size="sm" className="h-auto min-h-11 px-0" onClick={() => openWith(draftOf(occasion))}>
              Sửa
            </Button>
          ) : null}
        </div>
      ) : canWrite ? (
        <>
          <Button type="button" variant="outline" size="sm" className="min-h-11" onClick={() => openWith(EMPTY_DRAFT)}>
            🎂 Ghi dịp chụp / sinh nhật
          </Button>
          {mention ? (
            <p data-testid="dip-chup-goi-y" className="flex flex-wrap items-center gap-x-2 rounded-md bg-amber-50 px-2 py-1 text-xs">
              <span>
                Thấy “<b>{mention.match}</b>” trong ghi chú — lưu làm dịp chụp?
              </span>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto min-h-11 px-0"
                onClick={() => openWith(draftFromMention(suggestText) ?? EMPTY_DRAFT)}
              >
                Lưu sinh nhật này
              </Button>
            </p>
          ) : null}
        </>
      ) : (
        <span className="text-muted-foreground">Chưa ghi dịp chụp</span>
      )}
      {canWrite ? <OccasionDialog key={openSeq} orderId={orderId} open={open} onOpenChange={setOpen} initial={initial} /> : null}
    </div>
  );
}
